import {useEffect, useMemo, useRef, useState} from "react";

import {PLAYER_EVENTS, useAudioContext} from "./AudioContext";
import {
  findWaveformsDictionaryParaByPosition,
  getSeekChapter,
  isChapterInPlayer
} from "../utils/player";

// Expands AudioContext hook to get dynamic current time position of the playing audio.
// Note: Each {time} update invokes a render.
export const useAudioContextWithCurrentTime = (audioContext, {
  // To not listen too often when progress line is a slider one.
  delayUpdateTime,
}) => {
  const timeoutRef = useRef(-1);
  const {
    isPlaying,
    getPlayerCurrentTime,
    chapterId,
    subscribeToPlayerEvents,
    unsubscribeFromPlayerEvents,
  } = audioContext;
  const [time, setTime] = useState(getPlayerCurrentTime());

  // @UX - is needed to much faster time sync.
  useEffect(() => {
    const listener = ({event, position}) => {
      if (event === PLAYER_EVENTS.CAN_PLAY_THROUGH) {
        setTime(getPlayerCurrentTime());
      }

      if (event === PLAYER_EVENTS.PLAY_CHAPTER) {
        setTime(position);
      }
    };

    subscribeToPlayerEvents(listener);

    return () => {
      unsubscribeFromPlayerEvents(listener);
    };
  }, []);

  // Update {time} every {delayUpdateTime} if {enabled} and {isPlaying}.
  useEffect(() => {
    const updateCurrentTime = (setNewTimeout) => {
      setTime(getPlayerCurrentTime());

      if (setNewTimeout) {
        timeoutRef.current = setTimeout(() => updateCurrentTime(setNewTimeout), delayUpdateTime);
      }
    };

    updateCurrentTime(isPlaying);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [delayUpdateTime, isPlaying, chapterId]);

  const withInstantlyTimeUpdate = (method) => {
    return (...args) => {
      method(...args);
      setTime(getPlayerCurrentTime());
    };
  };

  return {
    ...audioContext,
    timePosition: time,
    seekPlayer: withInstantlyTimeUpdate(audioContext.seekPlayer),
    skipPlayer: withInstantlyTimeUpdate(audioContext.skipPlayer),
    setPlayerCurrentTime: withInstantlyTimeUpdate(audioContext.setPlayerCurrentTime),
  };
};

const DELAY_CODE_EXECUTIONS = 500;

/**
 * Returns immediately found current para and waits its remain time with {setTimeout}
 * to find a next para then.
 * @return {{
 *   id: string,
 *   start: number,
 *   end: number,
 * } | undefined}
 */
export const useGetCurrentPlayingPara = () => {
  const timerIdRef = useRef();
  const {
    isPlaying,
    getPlayerCurrentTime,
    playbackRate,
    subscribeToPlayerEvents,
    unsubscribeFromPlayerEvents,
    audioChapterId,
    waveformsDictionary,
  } = useAudioContext();

  const [triggerRedefineValue, setTriggerRedefineValue] = useState(0);

  // Find current paragraph and set timeout to search paragraph anew after its remain seconds.
  const currentPara = useMemo(() => {
    clearTimeout(timerIdRef.current);

    const playerPosition = getPlayerCurrentTime();
    const currentPara = findWaveformsDictionaryParaByPosition(
      waveformsDictionary, audioChapterId, playerPosition
    );

    if (currentPara && isPlaying) {
      const secondsRemain = ((currentPara.end - playerPosition) / playbackRate).toFixed(2);

      timerIdRef.current = setTimeout(() => {
        setTriggerRedefineValue(currentPara.id + secondsRemain + Math.random());
      }, secondsRemain * 1000 + DELAY_CODE_EXECUTIONS);
    }

    return currentPara;
  }, [
    triggerRedefineValue, waveformsDictionary,
    isPlaying, audioChapterId, playbackRate
  ]);

  // Clean up timer on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(timerIdRef.current);
    };
  }, []);

  // Additional cases to search paragraph anew.
  useEffect(() => {
    const listener = ({event}) => {
      if (event === PLAYER_EVENTS.PLAY_CHAPTER
      || event === PLAYER_EVENTS.CAN_PLAY_THROUGH
      || event === PLAYER_EVENTS.SKIP
      || event === PLAYER_EVENTS.SEEK) {
        setTriggerRedefineValue(Math.random());
      }
    };

    subscribeToPlayerEvents(listener);

    return () => {
      unsubscribeFromPlayerEvents(listener);
    };
  }, []);

  return currentPara;
};

/**
 * @typedef {Object} CurrPrevNextChapters
 * @property {{
 *     chapter: {} | null,
 *     audioChapter: {} | null,
 *     positions: {} | null,
 *   } | null} chapterCurr
 * @property {SeekChapter | null} chapterSeekNext
 * @property {SeekChapter | null} chapterSeekPrev
 */
export const useGetCurrPrevNextChapters = (
  isPlaying,
  playbackRate,
  getPlayerCurrentTime,
  audioChapterId,
  toc,
  waveformsDictionary,
  subscribeToPlayerEvents, unsubscribeFromPlayerEvents,
  downloadOrPullFromSWWaveformsData,
) => {
  const timerIdRef = useRef();

  const [triggerRedefineValue, setTriggerRedefineValue] = useState(0);
  const [chaptersIdsWaveformFetched, setChaptersIdsWaveformFetched] = useState([]);

  /**
   * Finds the current playing chapter.
   * Sets the timeout to define value anew.
   * @type {CurrPrevNextChapters}
   */
  const currPrevNextChaptersData = useMemo(() => {
    clearTimeout(timerIdRef.current);

    let chapterCurr = null, chapterSeekNext = null, chapterSeekPrev = null;

    const playerPosition = getPlayerCurrentTime();
    const indexChapterPlaying = toc.findIndex((chapter) => {
      const res = isChapterInPlayer(
        toc, chapter.id, waveformsDictionary, audioChapterId, playerPosition
      );
      if (res.isInPlayer) {
        chapterCurr = {
          chapter,
          positions: res.audioPositions,
          audioChapter: res.audioChapter,
        };
        return true;
      }
      return false;
    });

    if (indexChapterPlaying !== -1) {
      chapterSeekPrev = getSeekChapter(toc, indexChapterPlaying, waveformsDictionary, false);
      chapterSeekNext = getSeekChapter(toc, indexChapterPlaying, waveformsDictionary, true);
    }

    if (chapterCurr && chapterSeekNext && chapterSeekNext.audioChapter?.id === chapterCurr.audioChapter?.id && chapterSeekNext?.positions && isPlaying) {
      const secondsRemainByPosition = chapterSeekNext.positions.start - getPlayerCurrentTime();
      const secondsRemainFinal = (secondsRemainByPosition / playbackRate).toFixed(2);

      timerIdRef.current = setTimeout(() => {
        setTriggerRedefineValue(chapterCurr.chapter.id + secondsRemainFinal + Math.random());
      }, secondsRemainFinal * 1000 + DELAY_CODE_EXECUTIONS);
    }

    return {
      chapterCurr,
      chapterSeekNext,
      chapterSeekPrev,
    };
  }, [
    triggerRedefineValue, waveformsDictionary,
    isPlaying, audioChapterId, playbackRate,
    toc, waveformsDictionary,
  ]);

  // Fetches missing waveforms for potentially next and prev seek chapters.
  useEffect(() => {
    const {chapterSeekNext, chapterSeekPrev} = currPrevNextChaptersData;
    const chaptersWaveformDownloadNeeded = [chapterSeekNext, chapterSeekPrev].filter((d) => {
      if (d) {
        const {isMissingWaveform, audioChapter} = d;
        return (
          isMissingWaveform
          && audioChapter
          && !chaptersIdsWaveformFetched.includes(audioChapter.id)
        );
      }
      return false;
    });

    if (chaptersWaveformDownloadNeeded.length) {
      const chaptersIds = chaptersWaveformDownloadNeeded.map(({audioChapter}) => {
        downloadOrPullFromSWWaveformsData(audioChapter);
        return audioChapter.id;
      });
      setChaptersIdsWaveformFetched((v) => {
        return [...v, ...chaptersIds];
      });
    }
  }, [currPrevNextChaptersData]);

  // Clean up timer on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(timerIdRef.current);
    };
  }, []);

  // Additional cases to define chapters anew.
  useEffect(() => {
    const listener = ({event}) => {
      if (event === PLAYER_EVENTS.PLAY_CHAPTER
      || event === PLAYER_EVENTS.CAN_PLAY_THROUGH) {
        setTriggerRedefineValue(Math.random());
      }
    };

    subscribeToPlayerEvents(listener);

    return () => {
      unsubscribeFromPlayerEvents(listener);
    };
  }, []);

  return currPrevNextChaptersData;
};
